const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const buildTableOfContents = (root) => {
  const source = root.querySelector('[data-mf-toc-source]');
  const target = root.querySelector('[data-mf-toc]');
  if (!source || !target) return;

  const headings = [...source.querySelectorAll('h2, h3')].filter(
    (heading) => heading.textContent.trim().length > 0
  );

  if (!headings.length) {
    const wrapper = target.parentElement;
    if (wrapper) wrapper.hidden = true;
    return;
  }

  const usedIds = new Set();
  headings.forEach((heading, index) => {
    let id = heading.id || slugify(heading.textContent) || `section-${index + 1}`;
    const baseId = id;
    let count = 2;
    while (usedIds.has(id) || (document.getElementById(id) && document.getElementById(id) !== heading)) {
      id = `${baseId}-${count}`;
      count += 1;
    }
    usedIds.add(id);
    heading.id = id;

    const link = document.createElement('a');
    link.href = `#${id}`;
    link.textContent = heading.textContent.trim();
    link.dataset.level = heading.tagName.toLowerCase();
    target.append(link);
  });

  if (!('IntersectionObserver' in window)) return;

  const links = new Map([...target.querySelectorAll('a')].map((link) => [link.hash.slice(1), link]));
  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (!visible) return;
      links.forEach((link, id) => link.setAttribute('aria-current', id === visible.target.id ? 'true' : 'false'));
    },
    { rootMargin: '-20% 0px -68% 0px', threshold: [0, 1] }
  );
  headings.forEach((heading) => observer.observe(heading));
};

const setReadingProgress = (root) => {
  const progress = root.querySelector('[data-mf-reading-progress]');
  const article = root.querySelector('[data-mf-article-body]');
  if (!progress || !article) return;

  let scheduled = false;
  const update = () => {
    const bounds = article.getBoundingClientRect();
    const total = Math.max(article.offsetHeight - window.innerHeight, 1);
    const distance = Math.min(Math.max(-bounds.top, 0), total);
    progress.style.width = `${(distance / total) * 100}%`;
    scheduled = false;
  };

  const requestUpdate = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(update);
  };

  update();
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
};

const initializeCopyLinks = (root) => {
  root.querySelectorAll('[data-mf-copy-link]').forEach((button) => {
    button.addEventListener('click', async () => {
      const original = button.getAttribute('aria-label') || 'Copy link';
      try {
        await navigator.clipboard.writeText(window.location.href);
        button.setAttribute('aria-label', 'Link copied');
        button.textContent = '✓';
      } catch {
        window.prompt('Copy this link', window.location.href);
      }
      window.setTimeout(() => {
        button.setAttribute('aria-label', original);
        button.textContent = '↗';
      }, 1800);
    });
  });
};

const enhancePolicyPage = () => {
  const policy = document.querySelector('.shopify-policy__container');
  if (!policy) return;
  policy.classList.add('mf-policy-ready');
  policy.querySelectorAll('a[href^="http"]').forEach((link) => {
    if (link.hostname && link.hostname !== window.location.hostname) {
      link.rel = 'noopener';
    }
  });
};

const initialize = (scope = document) => {
  scope.querySelectorAll('.mf-system').forEach((root) => {
    if (root.dataset.mfReady === 'true') return;
    root.dataset.mfReady = 'true';
    buildTableOfContents(root);
    setReadingProgress(root);
    initializeCopyLinks(root);
  });
  enhancePolicyPage();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initialize());
} else {
  initialize();
}

document.addEventListener('shopify:section:load', (event) => initialize(event.target));
